import { useEffect, useRef } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { useSession } from '../session'
import { t } from '../i18n'
import { useSetupState } from '../live-ui/setup'
import { AppsButton } from './AppsMenu'
import { AccountMenu } from './AccountMenu'
import { BrandLogo, brandState, useBrandTab } from './Brand'
import { Heartbeat } from '../ui/motion'
import { Icon } from '../ui/Icon'
import { NODES_PATH } from '../admin/network/api'
import { LicenseBanner } from '../admin/LicenseBanner'
import { StatusBanner } from './StatusBanner'
import { ConsoleShell } from './ConsoleShell'

// The system's top-bar navigation (.sx-top nav): the current page is selection — accent tint and
// a 2 px accent edge below, never green (green = on air, AUDIT CB-02).
const tab = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap rounded-md px-3 py-1.5 text-sm ${
    isActive ? 'bg-accent/10 text-text shadow-[inset_0_-2px_0_var(--accent)]' : 'text-muted hover:text-text'
  }`

function LiveStatus() {
  const { conn, view } = useSession()
  const ok = conn === 'open' && view?.node === 'ok'
  const text =
    conn === 'connecting'
      ? 'CONNECTING…'
      : conn === 'reconnecting'
        ? 'RECONNECTING…'
        : conn === 'limited'
          ? 'LIVE PAUSED'
        : view?.node === 'unreachable'
          ? 'MEDIA SERVER DOWN'
          : 'CONNECTED'
  const long =
    conn === 'connecting'
      ? 'Connecting to live data'
      : conn === 'reconnecting'
        ? 'Live data: reconnecting'
        : conn === 'limited'
          ? t('studio.limited')
        : view?.node === 'unreachable'
          ? 'Media server not responding'
          : 'Live data connected'
  return (
    <span
      data-testid="live-status"
      data-ok={ok}
      role="status"
      title={long}
      className={`flex items-center gap-2 font-mono text-xs font-medium tracking-[.06em] ${ok ? 'text-live' : 'text-warn'}`}
    >
      {/* a still state icon: only the live signal may loop (07-motion, AUDIT CB-23) */}
      <Icon name={ok ? 'state-live' : 'state-warn'} size={12} />
      <span className="hidden md:inline">{text}</span>
      <span className="sr-only md:hidden">{long}</span>
    </span>
  )
}

/** Reopens the first-run guide (from step 1) on the Live screen. */
function SetupGuideButton() {
  const nav = useNavigate()
  const setup = useSetupState()
  return (
    <span className="flex items-center gap-2">
      {setup.error && <span role="alert" className="hidden text-xs text-fail md:inline">Couldn't open the guide</span>}
      <button
        type="button"
        data-testid="setup-guide"
        aria-label="Setup guide"
        title={setup.error ? `Couldn't open the guide: ${setup.error}. Click to try again.` : 'Open the first-time setup guide'}
        disabled={!!setup.busy}
        onClick={async () => {
          if (await setup.set('pending')) nav('/')
        }}
        className={`grid min-h-[44px] min-w-[44px] place-items-center rounded-md border px-0 text-sm font-medium md:min-h-9 md:px-3 ${
          setup.error ? 'border-fail/50 text-fail' : 'border-line-2 text-text-soft hover:text-text'
        }`}
      >
        <span aria-hidden className="md:hidden">?</span>
        <span className="hidden md:inline">Setup guide</span>
      </button>
    </span>
  )
}

export function Layout() {
  const { user, view, conn } = useSession()
  const admin = user?.role === 'admin'
  const loc = useLocation()
  const network = admin && loc.pathname.startsWith('/admin/network')
  // Live · Studio and Status fill the window under the header: their columns scroll, the page does not.
  const live = loc.pathname === '/' || loc.pathname === '/status'
  const brand = brandState({ view: user?.area === 'admin' ? null : view, conn, liveScreen: live })
  useBrandTab(brand)
  // The console (every /admin/* page of an admin): its own shell, the cabinet's header below stays as it is (CN-01).
  const consolePage = admin && (loc.pathname === '/admin' || loc.pathname.startsWith('/admin/'))
  // The tabs scroll sideways when they do not fit (the right group never wraps under them): keep the
  // current one in view. Spacing as the system's top bar (.sx-top: 2 px between tabs, 20 px gaps).
  const nav = useRef<HTMLElement>(null)
  useEffect(() => {
    nav.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [loc.pathname])
  if (consolePage) {
    return (
      // the console's logo is static (ConsoleBrand): no heartbeat in the header
      <Heartbeat logo={false}>
        <ConsoleShell status={<LiveStatus />} guide={user?.setup !== 'pending' && <SetupGuideButton />} />
      </Heartbeat>
    )
  }
  return (
    // the animated header logo is the view's one heartbeat: badge dots then hold still (src/ui/motion.tsx)
    <Heartbeat logo={brand !== 'static'}>
      <div className={live ? 'flex h-dvh flex-col max-md:h-auto max-md:min-h-dvh' : 'min-h-dvh'}>
        <header className={`border-b border-line ${live ? 'flex-none' : ''}`}>
          <div className="mx-auto flex max-w-[1376px] flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 md:px-8">
            <BrandLogo state={brand} />
            {/* CB-14: on a phone the Live screens keep the top bar and the ON AIR strip right under it; a
                user's only tab ("On air", the page itself) would be a row of its own there, so it hides
                (05 «Phone: top bar, ON AIR strip…»). Other pages keep it as the way back. */}
            <nav ref={nav} className={`order-3 -mx-1 flex w-full gap-0.5 overflow-x-auto md:order-none md:w-auto md:min-w-0 md:flex-1 ${live && !admin && user?.area !== 'admin' ? 'max-md:hidden' : ''}`} aria-label="Main">
              {user?.area === 'admin' && user.app_url ? (
                <a href={user.app_url} className={tab({ isActive: false })} data-testid="nav-cabinet">
                  Cabinet ↗
                </a>
              ) : (
                <Link to="/" className={tab({ isActive: live })} aria-current={live ? 'page' : undefined}>
                  On air
                </Link>
              )}
              {/* Admin pages on a separate admin name (docs/deploy.md «Имена»): one link there. */}
              {admin && user?.admin_url && (
                <a href={`${user.admin_url}/admin`} className={tab({ isActive: false })} data-testid="nav-admin">
                  Admin console ↗
                </a>
              )}
              {admin && !user?.admin_url && (
                <>
                  <span className="mx-1 w-px self-stretch bg-line" aria-hidden />
                  <NavLink to="/admin" end className={tab}>
                    Overview
                  </NavLink>
                  <Link to={NODES_PATH} className={tab({ isActive: network })} data-testid="nav-network" aria-current={network ? 'page' : undefined}>
                    Network
                  </Link>
                  <NavLink to="/admin/users" className={tab}>
                    Users
                  </NavLink>
                  <NavLink to="/admin/streams" className={tab}>
                    Streams
                  </NavLink>
                  <NavLink to="/admin/outputs" className={tab}>
                    Outputs
                  </NavLink>
                  <NavLink to="/admin/audit" className={tab}>
                    Log
                  </NavLink>
                  <NavLink to="/admin/status" className={tab} data-testid="nav-status-page">
                    Status page
                  </NavLink>
                  <NavLink to="/admin/settings" className={tab} data-testid="nav-settings">
                    Settings
                  </NavLink>
                  <NavLink to="/admin/license" className={tab} data-testid="nav-license">
                    License
                  </NavLink>
                </>
              )}
            </nav>
            <div className="ml-auto flex items-center gap-4">
              <LiveStatus />
              <AppsButton />
              {user?.setup !== 'pending' && <SetupGuideButton />}
              <AccountMenu />
            </div>
          </div>
        </header>
        {!live && <LicenseBanner />}
        {user && user.area !== 'admin' && (live ? <div className="flex-none"><StatusBanner /></div> : <StatusBanner />)}
        {live ? (
          <main className="flex min-h-0 flex-1 flex-col">
            <Outlet />
          </main>
        ) : (
          <main className="mx-auto max-w-[1376px] px-4 pt-4 pb-12 md:px-8 md:pt-7">
            <Outlet />
          </main>
        )}
      </div>
    </Heartbeat>
  )
}
